import { baseUrl } from "util/APIBaseUrl";
import { extractErrorCode, toApiResult } from "util/apiError";
import type { ApiResult } from "util/apiError";
import { plgRequest } from "plg/api/plgApi";
import { plgProofHeaders } from "plg/auth/deviceKey";
import { adjustClockFromServer, serverTimeFrom } from "plg/auth/clockOffset";
import { clearPlgSession, getPlgProfile, getPlgRefreshToken, updatePlgProfile } from "plg/auth/plgTokenStore";
import type { PlgTokens } from "plg/auth/plgTokenStore";
import { identifyPlg, identifySignedIn, resetPlgAnalytics } from "plg/analytics/identity";
import { rememberBlockedCountry } from "plg/analytics/blockedCountry";
import type {
  HumanCheckBody,
  HumanCheckResponse,
  MagicLinkRequestBody,
  MagicLinkPreviewResponse,
  MagicLinkRequestResponse,
  MagicLinkVerifyBody,
  PlgAuthConfig,
  PlgAuthStartBody,
  PlgAuthStartResponse,
  PlgMe,
  PlgSignupConfig,
  SignupAccountBody,
} from "plg/types";

/** A failed public GET, carrying the status and the portal error code (e.g. COUNTRY_NOT_SUPPORTED). */
export class PlgHttpError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function publicGet<T>(path: string): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`);
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    rememberBlockedCountry(payload);
    throw new PlgHttpError(`GET ${path} failed: ${res.status}`, res.status, extractErrorCode(payload));
  }
  return (await res.json()) as T;
}

/**
 * Public PLG POSTs all carry a device proof (v2.2), so the server can bind what it issues to this
 * browser. A proof rejected for the device clock is retried once with the server's time.
 */
async function publicPost<T>(path: string, body: unknown): Promise<ApiResult<T>> {
  const url = `${baseUrl}${path}`;
  const send = async () => {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await plgProofHeaders("POST", url)) },
      body: JSON.stringify(body),
    });
    return { ok: res.ok, status: res.status, payload: await res.json().catch(() => null) };
  };
  try {
    let out = await send();
    if (out.status === 401 && extractErrorCode(out.payload) === "PLG_PROOF_INVALID") {
      adjustClockFromServer(serverTimeFrom(out.payload));
      out = await send();
    }
    rememberBlockedCountry(out.payload);
    identifySignedIn(out.payload);
    return toApiResult<T>(out);
  } catch {
    return { ok: false, status: 0 };
  }
}

export const fetchSignupConfig = () => publicGet<PlgSignupConfig>("/v1/plg/signup-config");
export const fetchPlgAuthConfig = () => publicGet<PlgAuthConfig>("/v1/plg/auth-config");
/** The marketplace tool catalogue, for tool search on the sign-up tools step. */
export const fetchPlgTools = () => publicGet<{ name: string; category?: string; logoUrl?: string }[]>("/v1/plg/tools");
/** The Cloudflare gate on /signup: a Turnstile token in, a short-lived human pass out. */
export const submitHumanCheck = (body: HumanCheckBody) => publicPost<HumanCheckResponse>("/v1/plg/auth/human-check", body);
export const startPlgAuth = (body: PlgAuthStartBody) => publicPost<PlgAuthStartResponse>("/v1/plg/auth/start", body);
/** The first setup step: creates the account and returns its first session (setup still open). */
export const createPlgAccount = (body: SignupAccountBody) => publicPost<PlgTokens>("/v1/plg/signup/account", body);
export const requestMagicLink = (body: MagicLinkRequestBody) =>
  publicPost<MagicLinkRequestResponse>("/v1/plg/auth/magic-link", body);
/** Does not use up the link: returns the masked email and whether Continue signs up or logs in. */
export const previewMagicLink = (body: MagicLinkVerifyBody) =>
  publicPost<MagicLinkPreviewResponse>("/v1/plg/auth/magic-link/preview", body);
export const verifyMagicLink = (body: MagicLinkVerifyBody) =>
  publicPost<PlgAuthStartResponse>("/v1/plg/auth/magic-link/verify", body);

export async function fetchPlgMe(): Promise<PlgMe> {
  const { ok, status, payload } = await plgRequest<PlgMe>("GET", "/v1/plg/me");
  if (!ok) throw new Error(`Failed to load account: ${status}`);
  const { firstName, lastName, state, industry, size, companyName, isVerified, emailType, tier } = payload;
  updatePlgProfile({ firstName, lastName, state, industry, size, companyName, isVerified, emailType, tier });
  // The account traits now include what only /plg/me knows (email type); nothing re-renders on a profile write.
  const profile = getPlgProfile();
  if (profile) identifyPlg(profile);
  return payload;
}

export async function logoutPlg(): Promise<void> {
  try {
    await plgRequest("POST", "/v1/plg/auth/logout", { refreshToken: getPlgRefreshToken() });
  } catch {
    // Logging out locally is what matters; a failed server call only leaves a row that expires.
  }
  clearPlgSession();
  resetPlgAnalytics();
}
